<template>
  <div id="learner-progress">
    <div class="heading-container">
      <h1>Employer Summary</h1>
      <div v-if="canViewAllEmployers" class="employer-selector-container">
        <label for="employer-select">Employers:</label>
        <k-dropdown
          class="dropdown dark"
          placeholder="Select Employer"
          :showSearch="true"
          :options="employerOptions"
          v-model="selectedEmployerId"
          :disabled="!employersReady"
        ></k-dropdown>
      </div>
    </div>

    <div class="panel panel-body">
      <div class="employer-summary-chart-overview panel">
        <h3>Overview</h3>
        <em>Distribution of learners based on ILR status</em>
        <div class="overview-content">
          <div class="chart-container">
            <k-chart-pie
              :seriesData="learnerStatusChartData.series"
              :seriesLabels="learnerStatusChartData.labels"
              :showLegend="true"
              :customColors="employerTablePieChartColors"
            ></k-chart-pie>
          </div>
        </div>
      </div>
      <div class="employer-summary-programme-overview panel">
        <div class="lf-header">
          <h3>Programme Overview</h3>
        </div>
        <div class="lf-content">
          <div class="lf-overview">
            <div class="progress-container">
                <k-table
                  class="learner-progress-table"
                  :loading="!learnerProgressReady"
                  :rows="kableRows"
                  :headers="kableHeaders"
                  :defaultHeaders="visibleHeaders"
                  :max="7"
                  :hideable="false"
                  :panel="false"
                >
                </k-table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <employer-learners-table
      :panelTitle="'Active Learners'"
      :statusType="['Continuing Learning']"
      :learnerData="learnerProgress"
      :loading="!learnerProgressReady"
    ></employer-learners-table>
    <employer-learners-table
      :panelTitle="'Completed Learners'"
      :statusType="['Completed Learning']"
      :learnerData="learnerProgress"
      :loading="!learnerProgressReady"
    >
      <template v-slot:preContent>
        <p>The table below displays both successful EPA achievers and those who have referred. Please note that some individuals listed as not having achieved may be in the process of retaking their final EPA assessment.</p>
      </template>
    </employer-learners-table>
    <employer-learners-table
        :panelTitle="'BIL Learners'"
        :statusType="['On a Break']"
        :learnerData="learnerProgress"
        :loading="!learnerProgressReady"
    ></employer-learners-table>
    <employer-learners-table
      :panelTitle="'Withdrawn Learners'"
      :statusType="['Withdrawn']"
      :learnerData="learnerProgress"
      :loading="!learnerProgressReady"
    ></employer-learners-table>
  </div>
</template>

<style>
#learner-progress .panel {
  box-shadow: var(--box-shadow);
}
</style>

<style scoped>
.heading-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
}

.panel-body::before,
.panel-body::after {
  content: unset;
}

.panel-body {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 25px;
  padding: 15px;
}

.lf-header,
.header-actions,
.overview-stats,
.chart-legend {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
}

.overview-content hr {
  width: 25%;
  border: 1px solid var(--kate-background-alt);
}

.employer-summary-chart-overview.panel,
.employer-summary-programme-overview.panel {
  margin: 0;
  flex: 1;
  border-radius: 15px;
  padding-top: 5px;
}

.employer-summary-chart-overview em {
  font-size: 12px;
}

.employer-summary-programme-overview {
  flex: 1;
}

.overview-content,
.lf-overview {
  margin-top: 20px;
}

.lf-header {
  padding-top: 5px;
  align-items: flex-start;
}

.lf-overview {
  max-height: 220px;
  overflow-y: auto;
}

.progress-container.k-table-panel {
  width: 100%;
}

.chart-legend {
  margin-top: 15px;
}

.progress-container {
  gap: 25px;
}

.content-container {
  overflow-x: auto;
  flex: 1;
}

.employer-selector-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 15px 0;
}

.employer-selector-container label {
  font-weight: 600;
  color: var(--kate-type-light);
}

.employer-selector-container .dropdown {
  min-width: 250px;
}
</style>

<script>
import PageReadyMixin from '@mixins/page-ready-mixin';
import KTable from '@base-components/tables/display/k-table.vue';
import KChartPie from '@base-components/charts/k-chart-pie.vue';
import KDropdown from '@base-components/k-dropdown.vue';
import EmployerLearnersTable from './employer-learners-table.vue';

export default {
  name: 'EmployerLearnerSummary',

  mixins: [PageReadyMixin],

  components: {
    EmployerLearnersTable,
    KTable,
    KChartPie,
    'k-dropdown': KDropdown,
  },

  data() {
    return {
      employersReady: false,
      employers: [],
      selectedEmployerId: null,
      learnerProgress: [],
      learnerProgressReady: false,
      employerTablePieChartColors: ['var(--kate-chart-secondary)', 'var(--kate-chart-neutral)', 'var(--kate-chart-success)'],
    };
  },

  beforeMount() {
    if (this.canViewAllEmployers) {
      this.getEmployers();
    } else {
      this.getLearnerProgress();
    }
  },

  watch: {
    selectedEmployerId(newVal, oldVal) {
      if (oldVal !== null && newVal !== oldVal) {
        this.updateRouteQuery();
        this.getLearnerProgress();
      }
    },
  },

  computed: {
    employerIdFromRoute() {
      return this.$route.query.employerId ? Number(this.$route.query.employerId) : null;
    },

    ready() {
      if (this.canViewAllEmployers) {
        return this.learnerProgressReady && this.employersReady;
      }
      return this.learnerProgressReady;
    },

    employerOptions() {
      return this.employers.map(e => ({
        id: e.id,
        name: e.name,
      }));
    },

    canViewAllEmployers() {
      return this.$permissions.hasPermission('view_employers');
    },

    kableHeaders() {
      return {
        programme: {
          name: 'Programme Name',
          type: 'string',
          filterable: false,
          sortable: true,
          hideable: false,
        },
        learner_count: {
          name: 'Learner Count',
          type: 'string',
          filterable: false,
          sortable: true,
          hideable: false,
        },
      };
    },

    visibleHeaders() {
      return ['programme', 'learner_count'];
    },

    allValidStatuses() {
      return ['Continuing Learning', 'On a Break', 'Withdrawn', 'Completed Learning'];
    },

    filteredLearners() {
      return this.getLearnersByStatus(this.allValidStatuses);
    },

    programmeCounts() {
      const counts = {};
      this.filteredLearners.forEach(learner => {
        const programme = learner.programme;
        if (programme) { // Only consider non-null programme names
          if (!counts[programme]) {
            counts[programme] = 1;
          } else {
            counts[programme]++;
          }
        }
      });
      return counts;
    },

    kableRows() {
      return Object.keys(this.programmeCounts).map(programme => ({
        programme,
        learner_count: this.programmeCounts[programme],
      }));
    },

    learnerStatusCounts() {
      const counts = {
        Active: 0,
        Withdrawn: 0,
        Completed: 0,
      };

      this.filteredLearners.forEach(learner => {
        const status = learner.ilr_completion_status;
        if (status === 'Withdrawn') {
          counts.Withdrawn++;
        } else if (status === 'Completed Learning') {
          counts.Completed++;
        } else if (status === 'Continuing Learning' || status === 'On a Break') {
          // active learners are only Continuing Learning or On a Break
          counts.Active++;
        }
      });
      return counts;
    },

    learnerStatusChartData() {
      const counts = this.learnerStatusCounts;
      const labels = ['Active', 'Withdrawn', 'Completed'];
      const series = labels.map(label => counts[label]);
      return { labels, series };
    },
  },
  methods: {
    updateRouteQuery() {
      if (this.canViewAllEmployers && this.selectedEmployerId) {
        this.$router.replace({
          query: { ...this.$route.query, employerId: this.selectedEmployerId },
        });
      }
    },

    initializeEmployerSelection() {
      // Priority: URL param > first employer
      if (this.employerIdFromRoute) {
        const employer = this.employers.find(e => e.id === this.employerIdFromRoute);
        if (employer) {
          this.selectedEmployerId = employer.id;
          this.getLearnerProgress();
          return;
        }
      }
      if (this.employers.length > 0) {
        this.selectedEmployerId = this.employers[0].id;
        this.updateRouteQuery();
        this.getLearnerProgress();
      }
    },

    getLearnerProgress() {
      this.$Loading.start();
      this.learnerProgressReady = false;
      let url;
      if (this.canViewAllEmployers && this.selectedEmployerId) {
        // internal users
        url = `/api/stats/employers/${this.selectedEmployerId}/learners/progress`;
      } else {
        // for external employers
        url = '/api/stats/employers/learners/progress';
      }

      this.$logger.info('Getting learner progress for employer', { employerId: this.userId }, true);
      this.$http.get(url)
        .then(result => {
          this.$logger.info('Retrieved learners data for employer', {
            employerId: this.userId,
            learnerCount: result.data.length,
          });
          // Small correction to account for learners who have not made any OTJ yet
          // This would appear as a dash and empty rag otherwise

          this.learnerProgress = result.data?.map(learner => ({
            ...learner,
            perc_otj_hours: learner.perc_otj_hours || 0,
            workshops_completed_percentage: learner.workshops_completed_percentage || 0,
          }));
          const nameCounts = {};
          this.learnerProgress.forEach(learner => {
            if (learner.user_name) {
              if (!nameCounts[learner.user_name]) {
                nameCounts[learner.user_name] = 0;
              }
              nameCounts[learner.user_name] += 1;
            }
          });
          this.learnerProgress = this.learnerProgress.map(learner => {
            const name = learner.user_name;
            let modifiedUserName = name;

            if (name && nameCounts[name] > 1) {
              modifiedUserName = `${name} <i class="fas fa-question-circle header-tooltip-icon" title="Learner in multiple programmes" aria-hidden="true"></i>`;
            }

            return {
              ...learner,
              user_name: modifiedUserName,
            };
          });
        }).catch(err => {
          this.$ktoast.showError(err);
          this.$logger.autowarn('Error getting learners data for employer', { employerId: this.userId }, err);
        }).then(() => {
          this.learnerProgressReady = true;
          this.$Loading.finish();
        });
    },

    getLearnersByStatus(statuses) {
      return this.learnerProgress.filter(learner => {
        const status = learner.ilr_completion_status;
        return statuses.includes(status);
      });
    },

    getEmployers() {
      this.employersReady = false;
      this.$logger.info('Getting employers');
      this.$http.get('/api/profile/employers').then(res => {
        this.$logger.info('Successfully retrieved employers');
        this.employers = res.data.map(x => ({
          id: x.id,
          name: x.name,
        }));
        this.initializeEmployerSelection();
      }).catch(err => {
        this.$logger.error('Error retrieving employers');
        this.$ktoast.showError(err);
      }).then(() => {
        this.employersReady = true;
      });
    },
  },
};
</script>
